/** A generated attachment, including the snapshot needed by the optimistic message renderer. */
export interface RemixAttachment {
  readonly id: string;
  readonly public_url: string;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly variants?: Readonly<Record<string, unknown>>;
}

/** Server-owned lifecycle of one unpublished remix session. */
export interface RemixOperation {
  readonly id: string;
  readonly status: 'idle' | 'queued' | 'generating' | 'ready' | 'failed' | 'handed_off' | 'canceled';
  readonly attachment: RemixAttachment | null;
  readonly errorMessage?: string;
}

/** An expected transport or response failure, safe to show without provider details. */
export class RemixRequestError extends Error {
  /** Stable discriminator for expected API failures. */
  readonly _tag = 'RemixRequestError' as const;
}

/** Parsed outcome of a remix API call. */
export type RemixResult =
  | { readonly ok: true; readonly operation: RemixOperation }
  | { readonly ok: false; readonly error: RemixRequestError };

/** The authenticated app API boundary; provider credentials never reach the browser. */
export interface RemixApi {
  preview(input: {
    channelId: string;
    operationId: string;
    requestId: string;
    sourceAttachmentId: string;
    referenceAttachmentIds: readonly string[];
    prompt: string;
    signal: AbortSignal;
  }): Promise<RemixResult>;
  status(input: { channelId: string; operationId: string; signal: AbortSignal }): Promise<RemixResult>;
  handoff(input: { channelId: string; operationId: string; requestId: string; signal: AbortSignal }): Promise<RemixResult>;
  cancel(input: { channelId: string; operationId: string }): Promise<void>;
}

type Transport = {
  get(path: string, extra?: RequestInit): Promise<Response>;
  post(path: string, body: object, extra?: RequestInit): Promise<Response>;
  delete(path: string): Promise<Response>;
};

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function attachment(value: unknown): RemixAttachment | null {
  if (
    !record(value) ||
    typeof value.id !== 'string' ||
    !value.id ||
    typeof value.public_url !== 'string' ||
    !value.public_url ||
    !record(value.metadata)
  )
    return null;
  return {
    ...value,
    id: value.id,
    public_url: value.public_url,
    metadata: value.metadata,
    ...(record(value.variants) ? { variants: value.variants } : {}),
  };
}

function parseOperation(value: unknown): RemixOperation | null {
  if (!record(value) || typeof value.id !== 'string' || !value.id) return null;
  const status = value.status;
  if (
    status !== 'idle' &&
    status !== 'queued' &&
    status !== 'generating' &&
    status !== 'ready' &&
    status !== 'failed' &&
    status !== 'handed_off' &&
    status !== 'canceled'
  )
    return null;
  const image = attachment(value.attachment);
  if ((status === 'ready' || status === 'handed_off') && !image) return null;
  const errorMessage = typeof value.error_message === 'string' ? value.error_message.trim().slice(0, 1000) : '';
  return { id: value.id, status, attachment: image, ...(errorMessage ? { errorMessage } : {}) };
}

/** Construct a parsed, nonthrowing transport over the existing API v2 helpers. */
export function createImageRemixApi(transport: Transport): RemixApi {
  async function request(send: () => Promise<Response>): Promise<RemixResult> {
    try {
      const response = await send();
      const body: unknown = await response.json();
      const operation = response.ok && record(body) && body.ok === true && record(body.data) ? parseOperation(body.data.image_remix) : null;
      if (operation) return { ok: true, operation };
      const validationMessage =
        record(body) && record(body.validations)
          ? Object.values(body.validations)
              .flat()
              .find((value): value is string => typeof value === 'string' && Boolean(value.trim()))
          : undefined;
      const message =
        validationMessage?.slice(0, 1000) ||
        (response.status === 404
          ? 'This remix is no longer available. Close it and open Remix again.'
          : response.status === 401 || response.status === 403
            ? 'You no longer have permission to remix in this chat.'
            : 'Could not complete the remix request. Please try again.');
      return { ok: false, error: new RemixRequestError(message) };
    } catch {
      return { ok: false, error: new RemixRequestError('Could not reach the remix service. Please try again.') };
    }
  }
  const path = (channelId: string) => `/channels/${encodeURIComponent(channelId)}/image_remixes`;
  return {
    preview: (input) =>
      request(() =>
        transport.post(
          path(input.channelId),
          {
            image_remix: {
              operation_id: input.operationId,
              request_id: input.requestId,
              source_attachment_id: input.sourceAttachmentId,
              reference_attachment_ids: input.referenceAttachmentIds,
              prompt: input.prompt,
            },
          },
          { signal: input.signal },
        ),
      ),
    status: (input) =>
      request(() => transport.get(`${path(input.channelId)}/${encodeURIComponent(input.operationId)}`, { signal: input.signal })),
    handoff: (input) =>
      request(() =>
        transport.post(
          path(input.channelId),
          { image_remix: { operation_id: input.operationId, request_id: input.requestId, handoff: true } },
          { signal: input.signal },
        ),
      ),
    async cancel(input) {
      try {
        await transport.delete(`${path(input.channelId)}/${encodeURIComponent(input.operationId)}`);
      } catch {
        /* The pending-attachment purge is the cleanup backstop. */
      }
    },
  };
}

/** Modal state retains its last good image throughout generation and recoverable errors. */
export interface RemixView {
  readonly phase: 'editing' | 'previewing' | 'sending' | 'handoff-uncertain' | 'sent' | 'closed';
  readonly attachment: RemixAttachment | null;
  readonly error: string | null;
}

/** One attachment-only submission into the existing optimistic chat sender. */
export interface RemixMessage {
  readonly text: '';
  readonly channel_id: string;
  readonly workspace_id: string;
  readonly optimistic_sort_order: string;
  readonly attachments: readonly (RemixAttachment & { readonly attachment_id: string })[];
  readonly upload_ids: readonly string[];
}

/** Owns request identities, cancellation, polling, and handoff independently of React rendering. */
export class ImageRemixSession {
  private view: RemixView = { phase: 'editing', attachment: null, error: null };
  private listeners = new Set<() => void>();
  private controller: AbortController | null = null;
  private previewRequestId: string | null = null;
  private readonly handoffRequestId: string;
  private readonly operationId: string;
  private readonly messageId: string;

  /** Dependencies are injectable so the complete flow can be exercised without paid image calls. */
  constructor(
    private readonly options: {
      api: RemixApi;
      channelId: string;
      workspaceId: string;
      sourceAttachmentId: string;
      uuid: () => string;
      messageId: () => string;
      submit: (message: RemixMessage) => void;
      pause: (signal: AbortSignal) => Promise<void>;
    },
  ) {
    this.operationId = options.uuid();
    this.handoffRequestId = options.uuid();
    this.messageId = options.messageId();
  }

  /** Immutable snapshot for useSyncExternalStore and tests. */
  snapshot = (): RemixView => this.view;

  /** Subscribe to user-visible state changes. */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Start or reconnect to an explicitly requested preview; never retry a paid request automatically. */
  async preview(prompt: string, referenceAttachmentIds: readonly string[] = []): Promise<void> {
    if (this.view.phase !== 'editing' || !prompt.trim()) return;
    this.set({ ...this.view, phase: 'previewing', error: null });
    const controller = new AbortController();
    this.controller = controller;
    this.previewRequestId ||= this.options.uuid();
    let result = await this.options.api.preview({
      channelId: this.options.channelId,
      operationId: this.operationId,
      requestId: this.previewRequestId,
      sourceAttachmentId: this.options.sourceAttachmentId,
      referenceAttachmentIds,
      prompt: prompt.trim(),
      signal: controller.signal,
    });
    let pollFailures = 0;
    // Only status reads repeat automatically. Reusing the request id reconnects after an uncertain POST.
    while (!controller.signal.aborted) {
      if (!result.ok) {
        this.set({ ...this.view, phase: 'editing', error: result.error.message });
        return;
      }
      const operation = result.operation;
      if (operation.status !== 'queued' && operation.status !== 'generating') {
        this.previewRequestId = null;
        this.set({
          phase: 'editing',
          attachment: operation.attachment || this.view.attachment,
          error:
            operation.status === 'ready'
              ? null
              : operation.errorMessage || 'The preview could not be completed. Your last image is unchanged.',
        });
        return;
      }
      await this.options.pause(controller.signal);
      if (controller.signal.aborted) return;
      result = await this.options.api.status({
        channelId: this.options.channelId,
        operationId: this.operationId,
        signal: controller.signal,
      });
      if (!result.ok && ++pollFailures < 3) {
        result = { ok: true, operation };
      } else if (result.ok) pollFailures = 0;
    }
  }

  /** Reserve the displayed image, then hand it off once with a stable message identity. */
  async send(): Promise<boolean> {
    if (!this.view.attachment || (this.view.phase !== 'editing' && this.view.phase !== 'handoff-uncertain')) return false;
    this.set({ ...this.view, phase: 'sending', error: null });
    const controller = new AbortController();
    this.controller = controller;
    const result = await this.options.api.handoff({
      channelId: this.options.channelId,
      operationId: this.operationId,
      requestId: this.handoffRequestId,
      signal: controller.signal,
    });
    if (controller.signal.aborted) return false;
    if (!result.ok || !result.operation.attachment) {
      this.set({
        ...this.view,
        phase: 'handoff-uncertain',
        error: result.ok ? 'Could not prepare the image. Try Send again.' : result.error.message,
      });
      return false;
    }
    const image = result.operation.attachment;
    this.set({ ...this.view, phase: 'sent', error: null });
    this.options.submit({
      text: '',
      channel_id: this.options.channelId,
      workspace_id: this.options.workspaceId,
      optimistic_sort_order: this.messageId,
      attachments: [{ ...image, attachment_id: image.id }],
      upload_ids: [],
    });
    return true;
  }

  /** Cancel on close/navigation; a late response cannot publish into another chat. */
  close(): void {
    if (this.view.phase === 'closed') return;
    this.controller?.abort();
    const sent = this.view.phase === 'sent';
    this.set({ ...this.view, phase: 'closed' });
    if (!sent) void this.options.api.cancel({ channelId: this.options.channelId, operationId: this.operationId });
  }

  private set(view: RemixView): void {
    this.view = view;
    this.listeners.forEach((listener) => listener());
  }
}
